import {
  MODEL_DASHBOARD_COLUMNS,
  MODEL_DASHBOARD_CURRENT_VERSION,
  MODEL_DASHBOARD_DEFAULT_NEW_VIEW_NAME,
  MODEL_DASHBOARD_DEFAULT_SECTION_ID,
  MODEL_DASHBOARD_DEFAULT_SECTION_NAME,
  MODEL_DASHBOARD_DEFAULT_VIEW_NAME,
  MODEL_DASHBOARD_MAX_ROWS_HEIGHT
} from '@mpm-druid/constants';
import {
  CreateModelView,
  ModelViewDateRange,
  ModelViewSummary,
  PanelLayoutItem,
  PanelSectionLayout,
  UnsavedModelView
} from '@mpm-druid/types';

export function calculateAddPanelToLayout(
  id: string,
  width: number,
  height: number,
  layout: PanelSectionLayout
): PanelLayoutItem {
  width = Math.min(width, MODEL_DASHBOARD_COLUMNS);
  height = Math.min(height, MODEL_DASHBOARD_MAX_ROWS_HEIGHT);

  for (let y = 0; ; y++) {
    for (let x = 0; x <= MODEL_DASHBOARD_COLUMNS - width; x++) {
      const overlaps = layout.some(item => {
        return (
          x < item.x + item.w &&
          x + width > item.x &&
          y < item.y + item.h &&
          y + height > item.y
        );
      });

      if (!overlaps) {
        return { i: id, x, y, w: width, h: height };
      }
    }
  }
}

export function calculateDateRange(
  dateRange: ModelViewDateRange
): [string, string] {
  if (dateRange.lastDays) {
    return [
      new Date(
        Date.now() - dateRange.lastDays * 24 * 60 * 60 * 1000
      ).toISOString(),
      new Date().toISOString()
    ];
  }

  return [dateRange.dateStart, dateRange.dateEnd];
}

export function generateDefaultSummaryView(modelId: string): ModelViewSummary {
  return {
    id: null,
    name: MODEL_DASHBOARD_DEFAULT_VIEW_NAME,
    is_default: true,
    model_id: modelId,
    version: MODEL_DASHBOARD_CURRENT_VERSION,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    last_updated_by: 'comet'
  };
}

export function generateEmptyView(
  name = MODEL_DASHBOARD_DEFAULT_NEW_VIEW_NAME
): CreateModelView {
  return {
    name,
    data: {
      dateRange: {
        intervalType: 'DAILY',
        lastDays: 30,
        dateStart: null,
        dateEnd: null
      },
      filterBatchId: null,
      modelVersion: null,
      sections: [
        {
          id: MODEL_DASHBOARD_DEFAULT_SECTION_ID,
          isExpanded: true,
          items: [],
          layout: [],
          title: MODEL_DASHBOARD_DEFAULT_SECTION_NAME
        }
      ]
    },
    version: MODEL_DASHBOARD_CURRENT_VERSION
  };
}

export function generateDefaultView(modelId: string): UnsavedModelView {
  return {
    ...generateEmptyView(),
    ...generateDefaultSummaryView(modelId),
    id: null
  };
}

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

function isPlainObject(x: unknown) {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

export function mergeDeep<T>(target: T, patch: DeepPartial<T>): T {
  if (patch === undefined || patch === null || !isPlainObject(patch)) {
    return target;
  }

  const out = { ...target };
  let hasChanges = false;

  for (const key of Object.keys(patch) as Array<keyof T>) {
    const pVal = patch[key];
    const tVal = target[key];

    if (pVal === undefined || pVal === tVal) {
      continue;
    }

    if (isPlainObject(pVal) && isPlainObject(tVal)) {
      const result = mergeDeep(tVal, pVal);
      if (result !== tVal) {
        hasChanges = true;
        out[key] = result;
      }
    } else {
      out[key] = pVal as T[keyof T];
      hasChanges = true;
    }
  }

  return hasChanges ? out : target;
}
